import Image from 'next/image'

import { Button } from '@/components/_ui/Button'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import type { CosmicBelowFoldDistributorBannerType } from '@/lib/cms/cosmic/types/CosmicBelowFoldType'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { cn } from '@/lib/utils'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import useGlobalStore from '@/store/useGlobalStore'
import { getTerms } from '@/lib/hooks/services/useGetTerms'

type Props = {
  distributorBanner?: CosmicBelowFoldDistributorBannerType
  product: CosmicProductType
  selectedVariation: CosmicVariationType
}

export const DistributorBannerSection = ({
  distributorBanner,
  product,
  selectedVariation,
}: Props) => {
  const { gtm } = useGTM()
  const {
    app: { marketExceptions },
    user: { customerType },
  } = useGlobalStore()

  if (!distributorBanner) {
    return (
      <div className="w-full p-10">
        <ErrorAlert
          error={{
            error: {
              code: 500,
              message: 'Unable to load distributor banner section',
            },
          }}
        />
      </div>
    )
  }

  if (distributorBanner.hide) return null

  const left = distributorBanner.leftSection
  const right = distributorBanner.rightSection

  return (
    <div
      className="flex h-auto w-full flex-col lg:h-[43.8125rem] lg:w-full lg:flex-row"
      data-test="product-distributor-banner"
    >
      <div
        style={{
          backgroundColor: left.backgroundColor,
        }}
        className={cn(
          'flex h-1/2 w-full flex-col items-center justify-center py-20 lg:h-full lg:w-[55%] lg:items-end lg:py-0 lg:pr-48',
        )}
      >
        <span className="flex max-w-[28.25rem] flex-col items-center px-10">
          <h1
            className={cn(
              'w-full text-xl font-semibold leading-tight lg:text-[35px]',
              left.titleTextColor
                ? `text-[${left.titleTextColor}]`
                : 'text-primary',
            )}
            data-test="product-distributor-banner-title"
          >
            {left.title}
          </h1>
          <p
            className="mt-7 text-[15px] leading-7 text-primary lg:w-full lg:text-lg lg:leading-9"
            data-test="product-distributor-banner-text"
            dangerouslySetInnerHTML={{ __html: left.content }}
          />
          <a
            onClick={() => {
              gtm.productDetailDownloadPdfClicked({
                product,
                productVariation: selectedVariation,
              })

              mixpanelService
                .trackEvent(MixpanelEvent.PRODUCT_DETAIL_DOWNLOAD_PDF_CLICKED, {
                  item_sku: selectedVariation.sku,
                  item_brand: 'Unicity',
                  item_name: product.name,
                  category:
                    (product.categories && product.categories[0]?.name) || '',
                  price:
                    (selectedVariation.pricing &&
                      getTerms({
                        pricingData: selectedVariation.pricing,
                        forCustomerType: customerType,
                      })?.priceEach) ||
                    0,
                  currency: marketExceptions?.currencyCode || '',
                })
                .catch(() => {})
            }}
            href={left.downloadPdfButton.link}
            target="_blank"
          >
            <Button
              variant="secondary"
              className="mr-auto mt-8 rounded-full px-20 lg:mt-5"
              data-test="product-distributor-banner-left-button"
            >
              {left.downloadPdfButton.title}
            </Button>
          </a>
        </span>
      </div>

      <div
        style={{ backgroundColor: right.backgroundColor }}
        className="flex h-1/2 min-h-0 w-full flex-col items-center justify-center py-20 lg:h-full lg:w-[45%] lg:items-start lg:py-0 lg:pl-48"
      >
        <div className="max-w-[28.25rem]">
          <Image
            src={right.image}
            alt={right.button.title}
            height={290}
            width={240}
            data-test="product-distributor-banner-image"
          />

          <Button
            variant="default"
            className="mt-5 rounded-full px-20"
            onClick={async () => {
              window.scrollTo(0, 0)
            }}
            data-test="product-distributor-banner-right-button"
          >
            {right.button.title}
          </Button>
        </div>
      </div>
    </div>
  )
}
